import {
  VIEW_WIDTH_CHANGED_EVENT
} from 'gazelle/src/components/view-container/view-container.directive';

export const name = '$gzAppBroker';

/**
 * @ngdoc service
 * @name gazelle.components.appBroker.service:$gzAppBroker
 *
 * @description
 * The $gzAppBroker service keeps track of which applications are loaded in the client browser and provides an interface for other registered applications
 * to request that a specific application be shown, hidden, or get access to its public interface.
 *
 * The $gzAppBroker is primarily used by applications to request a mini-app show/hide itself or otherwise send messages to said mini-app.
 *
 * Applications should register themselves during their (AngularJS)run phase using $gzAppBroker.register('my-app-id', appInterface).
 *
 * An application that needs to send message to another application should use $gzAppBroker.interface('other-app-id') to retrieve the other applications
 * public interface.
 *
 * To cause an application to shown or hidden use the $gzAppBroker.show('other-app-id', [onShowParam]) and $gzAppBroker.hide('other-app-id').
 *
 * Before showing (opening) an app, if the app has a registered load function, the app's load function will be called.
 *
 * After showing (opening) an app, if the app has a registered onShow function, the app's onShow function will be called with the optional onShowParam.
 *
 * @usage
 * ```js
 * // In 'my-apps' module.run method:
 * $gzAppBroker.register('my-app-id', {load: loadFunction, onShow: onShowFunction});
 * ```
 */
export default function $gzAppBroker($log, $mdSidenav, $rootScope) {
  //noinspection BadExpressionStatementJS
  'ngInject';

  var vm = this;
  vm.apps = {};

  var service = {
    register: register,
    load: load,
    hasBeenLoaded: hasBeenLoaded,
    show: show,
    hide: hide,
    toggle: toggle,
    isShown: isShown,
    appInterface: appInterface,
  };

  return service;

  //
  // Public functions
  //

  /**
   * @ngdoc function
   * @name register
   * @methodOf gazelle.components.appBroker.service:$gzAppBroker
   * @description Registers an app
   * @param {string} appId - The ID of the app
   * @param {Object} appInterface - A JSON object that contains the app's public interfaces (i.e. "{load: myLoadFunction}")
   */
  function register(appId, appInterface) {
    $log.debug('gzAppBroker register called for appId = ' + appId);
    vm.apps[appId] = {};
    vm.apps[appId].appId = appId;
    vm.apps[appId].hasBeenLoaded = false;
    vm.apps[appId].appInterfaces = appInterface;
  } //register


  /**
   * @ngdoc function
   * @name load
   * @methodOf gazelle.components.appBroker.service:$gzAppBroker
   * @description Loads an app. Results in the app's "load" function to be called (if one exists).
   * @param {string} appId - The ID of the app
   */
  function load(appId) {
    $log.debug('gzAppBroker load called for appId = ' + appId);
    if (appId in vm.apps &&
        vm.apps[appId].appInterfaces &&
        vm.apps[appId].appInterfaces.load)
    {
      vm.apps[appId].appInterfaces.load();

      // TODO: Should we require the app's load function to return a promise and also return the promise here?

      vm.apps[appId].hasBeenLoaded = true;
    }
    else if (appId in vm.apps) {
      vm.apps[appId].hasBeenLoaded = false;
    }
  } //load


  /**
   * @ngdoc function
   * @name hasBeenLoaded
   * @methodOf gazelle.components.appBroker.service:$gzAppBroker
   * @description Determine if an app has been loaded yet
   * @param {string} appId - The ID of the app
   * @returns {boolean} a flag indicating if the app has been loaded
   */
  function hasBeenLoaded(appId) {
    return (appId in vm.apps) && vm.apps[appId].hasBeenLoaded;
  } //hasBeenLoaded


  /**
   * @ngdoc function
   * @name show
   * @methodOf gazelle.components.appBroker.service:$gzAppBroker
   * @description Causes an app to be shown (opened)
   * @param {string} appId - The ID of the app
   * @param {Object} [onShowParam] - An optional param that will be passed to the onShow function, if registered
   */
  function show(appId, onShowParam) {
    let openAppId = _getOpenApp();
    if (openAppId) {
      return hide(openAppId).then(() => {
        return _openApp(appId, onShowParam);
      });
    }
    else {
      return _openApp(appId, onShowParam);
    }
  } //show



  /**
   * @ngdoc function
   * @name hide
   * @methodOf gazelle.components.appBroker.service:$gzAppBroker
   * @description Causes an app to be hidden (closed)
   * @param {string} appId - The ID of the app
   */
  function hide(appId) {
    //$log.debug('gzAppBroker hide called for appId = ' + appId);
    let wasLockedOpen = $mdSidenav(appId).isLockedOpen();

    return $mdSidenav(appId).close().then(() => {
      //$log.debug('gzAppBroker hide finished = ' + appId);
      if(wasLockedOpen) {
        $rootScope.$emit(VIEW_WIDTH_CHANGED_EVENT, {
          source: name,
          expanded: false
        });
      }
    });
  } //hide


  /**
   * @ngdoc function
   * @name toggle
   * @methodOf gazelle.components.appBroker.service:$gzAppBroker
   * @description Causes an app to be toggled. Open if currently closed; close if currently open.
   * @param {string} appId - The ID of the app
   * @param {Object} [onShowParam] - An optional param that will be passed to the onShow function, if registered
   */
  function toggle(appId, onShowParam) {
    //$log.debug('gzAppBroker toggle called for appId = ' + appId);
    if ($mdSidenav(appId).isOpen()) {
      return hide(appId);
    }
    else {
      return show(appId, onShowParam);
    }
  } //toggle


  /**
   * @ngdoc function
   * @name isShown
   * @methodOf gazelle.components.appBroker.service:$gzAppBroker
   * @description Determine if an app is currently being shown
   * @param {string} appId - The ID of the app
   * @returns {boolean} a flag indicating if the app is currently being shown
   */
  function isShown(appId) {
    //$log.debug('gzAppBroker isShown ', appId, $mdSidenav(appId).isOpen(), transitionState);
    return (vm.apps[appId] && $mdSidenav(appId).isOpen());
  } //isShown

  /**
   * @ngdoc function
   * @name appInterface
   * @methodOf gazelle.components.appBroker.service:$gzAppBroker
   * @description Retrieve the public interfaces for the app
   * @param {string} appId - The ID of the app
   * @returns {Object} the public interfaces for the app
   */
  function appInterface(appId) {
    //$log.debug('gzAppBroker appInterface called for appId = ' + appId);
    if (appId in vm.apps) {
      return vm.apps[appId].appInterfaces;
    }
  } //appInterface


  //
  // Private functions
  //
  function _openApp(appId, onShowParam) {
    if (!hasBeenLoaded(appId)) {
      load(appId);
    }

    return $mdSidenav(appId).open().then(() => {
      if($mdSidenav(appId).isLockedOpen()) {
        $rootScope.$emit(VIEW_WIDTH_CHANGED_EVENT, {
          source: name,
          expanded: true
        });
      }
      _callOnShowIfRegistered(appId, onShowParam);
    });
  }

  function _getOpenApp() {
    let appId = null;
    _.forEach(vm.apps, function(app) {
      if ($mdSidenav(app.appId).isOpen()) {
        appId = app.appId;
      }
    });
    return appId;
  }

  function _callOnShowIfRegistered(appId, onShowParam) {
    if (appId in vm.apps &&
        vm.apps[appId].appInterfaces &&
        vm.apps[appId].appInterfaces.onShow)
    {
      vm.apps[appId].appInterfaces.onShow(onShowParam);
    }
  }

} //$gzAppBroker
